import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Row,
  Col,
  Card,
  Typography,
  Alert,
  Space,
  Checkbox,
  Divider,
  message,
  Tag,
  Table,
} from "antd";
import {
  TeamOutlined,
  CalculatorOutlined,
  CheckOutlined,
  CloseOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import { PayrollPeriod, PayrollEmployee } from "../../../types/hr";
import { formatCurrency } from "../utils";

const { Title, Text } = Typography;
const { Option } = Select;

interface BulkVariablesModalProps {
  visible: boolean;
  period: PayrollPeriod | null;
  employees: PayrollEmployee[];
  onCancel: () => void;
  onApply: (operation: BulkOperation) => Promise<void>;
}

interface BulkOperation {
  type: "attendance" | "overtime" | "bonus" | "deduction";
  operation: string;
  data: any;
  criteria: {
    employee_ids?: number[];
    department_ids?: number[];
    position_ids?: number[];
    all_employees?: boolean;
  };
}

interface EmployeeSelection {
  employee_id: number;
  employee_name: string;
  department_name?: string;
  position_name?: string;
  selected: boolean;
}

const BulkVariablesModal: React.FC<BulkVariablesModalProps> = ({
  visible,
  period,
  employees,
  onCancel,
  onApply,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [operationType, setOperationType] = useState<
    "attendance" | "overtime" | "bonus" | "deduction"
  >("attendance");
  const [operation, setOperation] = useState<string>("");
  const [employeeSelections, setEmployeeSelections] = useState<
    EmployeeSelection[]
  >([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [positions, setPositions] = useState<any[]>([]);
  const openedScope = useRef<string | null>(null);

  useEffect(() => {
    if (!visible) { openedScope.current = null; return; }
    const scope = `${period?.id}:${period?.company_id}`;
    const isOpening = openedScope.current !== scope;
    openedScope.current = scope;
    if (visible) {
      // Initialiser les sélections d'employés
      const selections = employees.map((emp) => ({
        employee_id: emp.employee_id,
        employee_name: `${emp.first_name} ${emp.last_name}`,
        department_name: emp.department_name,
        position_name: emp.position_name,
        selected: false,
      }));
      setEmployeeSelections(previous => {
        if (isOpening) return selections;
        const selectedIds = new Set(previous.filter(employee => employee.selected).map(employee => employee.employee_id));
        const currentIds = new Set(selections.map(employee => employee.employee_id));
        return [...selections.map(employee => ({ ...employee, selected: selectedIds.has(employee.employee_id) })),
          ...previous.filter(employee => employee.selected && !currentIds.has(employee.employee_id))];
      });

      // Extraire départements et postes uniques
      const uniqueDepts = Array.from(
        new Set(employees.map((emp) => emp.department_name).filter(Boolean))
      ).map((name) => ({ name, id: name }));
      const uniquePositions = Array.from(
        new Set(employees.map((emp) => emp.position_name).filter(Boolean))
      ).map((name) => ({ name, id: name }));

      setDepartments(uniqueDepts);
      setPositions(uniquePositions);
    }
  }, [visible, employees, period?.id, period?.company_id]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();

      const selectedEmployees = employeeSelections
        .filter((emp) => emp.selected)
        .map((emp) => emp.employee_id);

      if (selectedEmployees.length === 0) {
        message.warning("Veuillez sélectionner au moins un employé");
        return;
      }

      const bulkOperation: BulkOperation = {
        type: operationType,
        operation,
        data: values,
        criteria: {
          employee_ids: selectedEmployees,
          all_employees: selectedEmployees.length === employees.length,
        },
      };

      await onApply(bulkOperation);
      message.success(
        `Opération appliquée à ${selectedEmployees.length} employé(s)`
      );
      onCancel();
      form.resetFields();
    } catch (error) {
      console.error("Error in bulk operation:", error);
      message.error(error instanceof Error ? error.message : "Erreur lors de l'opération en masse");
    } finally {
      setLoading(false);
    }
  };

  const selectAllEmployees = () => {
    setEmployeeSelections((prev) =>
      prev.map((emp) => ({ ...emp, selected: true }))
    );
  };

  const clearAllSelections = () => {
    setEmployeeSelections((prev) =>
      prev.map((emp) => ({ ...emp, selected: false }))
    );
  };

  const selectByDepartment = (departmentName: string) => {
    setEmployeeSelections((prev) =>
      prev.map((emp) => ({
        ...emp,
        selected: emp.department_name === departmentName ? true : emp.selected,
      }))
    );
  };

  const selectByPosition = (positionName: string) => {
    setEmployeeSelections((prev) =>
      prev.map((emp) => ({
        ...emp,
        selected: emp.position_name === positionName ? true : emp.selected,
      }))
    );
  };

  const handleEmployeeSelection = (employeeId: number, selected: boolean) => {
    setEmployeeSelections((prev) =>
      prev.map((emp) =>
        emp.employee_id === employeeId ? { ...emp, selected } : emp
      )
    );
  };

  const getOperationFields = () => {
    switch (operationType) {
      case "attendance":
        return (
          <>
            <Form.Item
              name="operation"
              label="Type d'opération"
              rules={[
                { required: true, message: "Sélectionnez une opération" },
              ]}
            >
              <Select
                placeholder="Choisir une opération"
                onChange={setOperation}
              >
                <Option value="set_worked_days">
                  Définir jours travaillés
                </Option>
                <Option value="add_vacation">Ajouter congés</Option>
                <Option value="add_sick_leave">Ajouter arrêts maladie</Option>
                <Option value="add_unpaid_leave">
                  Ajouter absences non payées
                </Option>
              </Select>
            </Form.Item>

            {operation === "set_worked_days" && (
              <Form.Item
                name="worked_days"
                label="Nombre de jours travaillés"
                rules={[
                  { required: true, message: "Saisissez le nombre de jours" },
                ]}
              >
                <InputNumber
                  min={0}
                  max={31}
                  placeholder="Ex: 22"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            )}

            {(operation === "add_vacation" ||
              operation === "add_sick_leave" ||
              operation === "add_unpaid_leave") && (
              <Form.Item
                name="days"
                label="Nombre de jours"
                rules={[
                  { required: true, message: "Saisissez le nombre de jours" },
                ]}
              >
                <InputNumber
                  min={0}
                  max={31}
                  placeholder="Ex: 2"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            )}
          </>
        );

      case "overtime":
        return (
          <>
            <Form.Item
              name="operation"
              label="Type d'opération"
              rules={[
                { required: true, message: "Sélectionnez une opération" },
              ]}
            >
              <Select
                placeholder="Choisir une opération"
                onChange={setOperation}
              >
                <Option value="add_overtime">
                  Ajouter heures supplémentaires
                </Option>
              </Select>
            </Form.Item>

            {operation === "add_overtime" && (
              <>
                <Form.Item
                  name="hours"
                  label="Nombre d'heures supplémentaires"
                  rules={[
                    { required: true, message: "Saisissez le nombre d'heures" },
                  ]}
                >
                  <InputNumber
                    min={0}
                    max={100}
                    step={0.5}
                    placeholder="Ex: 10"
                    style={{ width: "100%" }}
                  />
                </Form.Item>

                <Form.Item
                  name="rate"
                  label="Taux de majoration"
                  rules={[{ required: true, message: "Saisissez le taux" }]}
                >
                  <InputNumber
                    min={1}
                    max={3}
                    step={0.1}
                    placeholder="Ex: 1.5"
                    style={{ width: "100%" }}
                  />
                </Form.Item>

                <Form.Item name="description" label="Description">
                  <Input.TextArea
                    rows={2}
                    placeholder="Description des heures supplémentaires..."
                  />
                </Form.Item>
              </>
            )}
          </>
        );

      case "bonus":
        return (
          <>
            <Form.Item
              name="operation"
              label="Type d'opération"
              rules={[
                { required: true, message: "Sélectionnez une opération" },
              ]}
            >
              <Select
                placeholder="Choisir une opération"
                onChange={setOperation}
              >
                <Option value="add_bonus">Ajouter prime</Option>
                <Option value="add_allowance">Ajouter indemnité</Option>
                <Option value="add_commission">Ajouter commission</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="amount"
              label="Montant"
              rules={[{ required: true, message: "Saisissez le montant" }]}
            >
              <InputNumber
                min={0}
                step={1000}
                placeholder="Ex: 50000"
                style={{ width: "100%" }}
              />
            </Form.Item>

            <Form.Item name="description" label="Description">
              <Input.TextArea
                rows={2}
                placeholder="Description de la prime/indemnité..."
              />
            </Form.Item>
          </>
        );

      case "deduction":
        return (
          <>
            <Form.Item
              name="operation"
              label="Type d'opération"
              rules={[
                { required: true, message: "Sélectionnez une opération" },
              ]}
            >
              <Select
                placeholder="Choisir une opération"
                onChange={setOperation}
              >
                <Option value="add_advance">Ajouter avance</Option>
                <Option value="add_loan_deduction">Ajouter retenue prêt</Option>
                <Option value="add_other_deduction">
                  Ajouter autre déduction
                </Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="amount"
              label="Montant"
              rules={[{ required: true, message: "Saisissez le montant" }]}
            >
              <InputNumber
                min={0}
                step={1000}
                placeholder="Ex: 25000"
                style={{ width: "100%" }}
              />
            </Form.Item>

            <Form.Item name="description" label="Description">
              <Input.TextArea
                rows={2}
                placeholder="Description de la déduction..."
              />
            </Form.Item>
          </>
        );

      default:
        return null;
    }
  };

  const selectedCount = employeeSelections.filter((emp) => emp.selected).length;

  const employeeColumns = [
    {
      title: (
        <Checkbox
          checked={selectedCount === employees.length}
          indeterminate={selectedCount > 0 && selectedCount < employees.length}
          onChange={(e) => {
            if (e.target.checked) {
              selectAllEmployees();
            } else {
              clearAllSelections();
            }
          }}
        >
          Tous
        </Checkbox>
      ),
      dataIndex: "selected",
      width: 80,
      render: (selected: boolean, record: EmployeeSelection) => (
        <Checkbox
          checked={selected}
          onChange={(e) =>
            handleEmployeeSelection(record.employee_id, e.target.checked)
          }
        />
      ),
    },
    {
      title: "Employé",
      dataIndex: "employee_name",
      render: (name: string, record: EmployeeSelection) => (
        <div>
          <Text strong>{name}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: "11px" }}>
            {record.department_name || "N/A"} - {record.position_name || "N/A"}
          </Text>
        </div>
      ),
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <CalculatorOutlined />
          <Title level={4} style={{ margin: 0 }}>
            Opérations en Masse - {period?.name}
          </Title>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      width={900}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Annuler
        </Button>,
        <Button
          key="apply"
          type="primary"
          onClick={handleSubmit}
          loading={loading}
          disabled={selectedCount === 0}
        >
          Appliquer à {selectedCount} employé(s)
        </Button>,
      ]}
    >
      <Row gutter={16}>
        {/* Configuration de l'opération */}
        <Col span={12}>
          <Card title="Configuration de l'Opération" size="small">
            <Form form={form} layout="vertical">
              <Form.Item label="Type de variable">
                <Select
                  value={operationType}
                  onChange={setOperationType}
                  style={{ width: "100%" }}
                >
                  <Option value="attendance">Présences</Option>
                  <Option value="overtime">Heures supplémentaires</Option>
                  <Option value="bonus">Primes & Indemnités</Option>
                  <Option value="deduction">Déductions</Option>
                </Select>
              </Form.Item>

              {getOperationFields()}
            </Form>
          </Card>
        </Col>

        {/* Sélection des employés */}
        <Col span={12}>
          <Card
            title={`Sélection des Employés (${selectedCount}/${employees.length})`}
            size="small"
            extra={
              <Space>
                <Button
                  size="small"
                  onClick={clearAllSelections}
                  icon={<CloseOutlined />}
                >
                  Aucun
                </Button>
                <Button
                  size="small"
                  onClick={selectAllEmployees}
                  icon={<CheckOutlined />}
                >
                  Tous
                </Button>
              </Space>
            }
          >
            {/* Sélection rapide */}
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary">Sélection rapide:</Text>
              <br />
              <Space wrap size="small" style={{ marginTop: 4 }}>
                {departments.map((dept) => (
                  <Button
                    key={dept.name}
                    size="small"
                    onClick={() => selectByDepartment(dept.name)}
                  >
                    {dept.name}
                  </Button>
                ))}
              </Space>
            </div>

            <Divider style={{ margin: "8px 0" }} />

            {/* Liste des employés */}
            <Table
              dataSource={employeeSelections}
              columns={employeeColumns}
              rowKey="employee_id"
              size="small"
              pagination={false}
              scroll={{ y: 200 }}
            />
          </Card>
        </Col>
      </Row>

      {/* Aperçu de l'opération */}
      {operation && selectedCount > 0 && (
        <Alert
          message="Aperçu de l'opération"
          description={`Cette opération sera appliquée à ${selectedCount} employé(s) sélectionné(s).`}
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />
      )}
    </Modal>
  );
};

export default BulkVariablesModal;
